import { useRef, useState } from 'react'
import ErkennungsKarte, { vorauswahl } from '../komponenten/Erkennung'
import { erkennungErneut, holeErkennung } from '../lib/warteschlangen'
import MenschKarte from '../komponenten/MenschKarte'
import Stapel from '../komponenten/Stapel'
import { useBild } from '../lib/bild'
import { datum, punkteSatz, useLaden } from '../lib/laden'
import {
  GRUENDE_FOTO,
  fotoAblehnen,
  fotoFreigeben,
  holeFotos,
  type Foto,
  pflichtGrund,
} from '../lib/warteschlangen'
import Bildfehler from '../komponenten/Bildfehler'
import Ladehinweis from '../komponenten/Ladehinweis'

/** Warteschlange „Fotos" (§8.2). */
export default function Fotos() {
  const [zustand, neuLaden] = useLaden(holeFotos)
  // Die angehakten Merkmale je Foto. Sie gehen mit der Entscheidung hinaus,
  // deshalb liest die Aktion sie über eine Referenz — beim Auslösen, nicht
  // beim Rendern.
  const [wahl, setWahl] = useState<Record<string, string[]>>({})
  const wahlRef = useRef(wahl)
  wahlRef.current = wahl

  if (zustand.art !== 'da') return <Ladehinweis zustand={zustand} />

  /** Noch nichts angefasst: die Vorauswahl des Modells gilt. */
  const bilderVon = (f: Foto) => [{ pfad: f.storage_path, erkennung: f.erkennung }]
  const merkmaleVon = (f: Foto): string[] =>
    wahlRef.current[f.id] ?? vorauswahl(bilderVon(f), f.ort_features)

  return (
    <Stapel<Foto>
      titel="Fotos"
      eintraege={zustand.wert.eintraege}
      gesamt={zustand.wert.gesamt}
      schluessel={(f) => f.id}
      neuLaden={neuLaden}
      gruende={GRUENDE_FOTO}
      leerText="Kein Foto wartet auf eine Entscheidung."
      zeile={(f) => (
        <>
          <span className="stapel-zeile-haupt">
            {f.ort_name ?? 'Ort ohne Namen'}
          </span>
          <span className="leise">{datum(f.uploaded_at ?? f.created_at)}</span>
        </>
      )}
      inhalt={(f) => (
        <FotoAnsicht
          foto={f}
          gewaehlt={wahl[f.id] ?? vorauswahl(bilderVon(f), f.ort_features)}
          setzen={(m) => setWahl((w) => ({ ...w, [f.id]: m }))}
        />
      )}
      aktion={{
        freigeben: (f) => fotoFreigeben(f.id, merkmaleVon(f)),
        ablehnen: (f, code) => fotoAblehnen(f.id, pflichtGrund(code), merkmaleVon(f)),
        bericht: (f, ergebnis, art) => {
          const name =
            f.mensch?.username ??
            f.mensch?.display_name ??
            `Anonym · ${(f.beitragender ?? '').slice(0, 6)}`
          const was = art === 'frei' ? 'Foto freigegeben' : 'Foto abgelehnt'
          // Zurückgelesen: die Merkmale, die tatsächlich geschrieben wurden.
          const geschrieben = ergebnis['merkmale']
          const teile = [was]
          if (Array.isArray(geschrieben) && geschrieben.length > 0) {
            teile.push(
              `${geschrieben.length} ${geschrieben.length === 1 ? 'Merkmal' : 'Merkmale'} am Ort ergänzt`,
            )
          }
          teile.push(punkteSatz(ergebnis.punkte.differenz, name))
          return teile.join(' · ')
        },
      }}
    />
  )
}

function FotoAnsicht({
  foto,
  gewaehlt,
  setzen,
}: {
  foto: Foto
  gewaehlt: string[]
  setzen: (schluessel: string[]) => void
}) {
  // Der Entscheidungsbildschirm zeigt das **Original**, ohne Rückfall auf die
  // 400er-Variante: Über den Inhalt eines Fotos urteilt man nicht an einem
  // Vorschaubild. Fehlt das Original, ist das ein Befund und wird als solcher
  // gemeldet — die kleine Variante ersatzweise zu zeigen, würde ihn verdecken.
  // (So bestätigt vom Schema-Chat am 18.08.2026.)
  const bild = useBild(foto.storage_path)

  return (
    <div className="ansicht">
      <div className="karte bild-karte">
        {bild.art === 'laedt' && <p className="gedaempft">Bild lädt …</p>}
        {bild.art === 'fehler' && (
          <Bildfehler
            zustand={bild}
            zusatz="Über den Inhalt lässt sich so nicht entscheiden."
          />
        )}
        {bild.art === 'da' && (
          <img className="bild-gross" src={bild.adresse} alt="Eingereichtes Foto" />
        )}
        <p className="leise zahl bild-pfad">{foto.storage_path}</p>
      </div>

      <div className="karte">
        <h2>{foto.ort_name ?? 'Ort ohne Namen'}</h2>
        <p className="gedaempft">
          {[foto.ort_strasse, [foto.ort_plz, foto.ort_stadt].filter(Boolean).join(' ')]
            .filter(Boolean)
            .join(', ') || 'keine Adresse hinterlegt'}
        </p>
        <p className="gedaempft ansicht-zeile">
          Aufgenommen: {datum(foto.uploaded_at ?? foto.created_at)}
        </p>
        <p className={foto.bilder_am_ort > 0 ? 'gedaempft' : 'leise'}>
          {foto.bilder_am_ort === 0
            ? 'Noch kein Bild an diesem Ort.'
            : `${foto.bilder_am_ort} ${foto.bilder_am_ort === 1 ? 'Bild hängt' : 'Bilder hängen'} schon an diesem Ort.`}
        </p>
      </div>

      {/* Nur an einem Ort: Ohne place_id gäbe es nichts, woran die Merkmale
          geschrieben würden. */}
      {foto.place_id && (
        <ErkennungsKarte
          bilder={[{ pfad: foto.storage_path, erkennung: foto.erkennung }]}
          features={foto.ort_features}
          umfeld={foto.ort_accessibility}
          gewaehlt={gewaehlt}
          setzen={setzen}
          nachladen={async () => [
            { pfad: foto.storage_path, erkennung: await holeErkennung(foto.id) },
          ]}
          erneut={async () => {
            await erkennungErneut(foto.id)
          }}
        />
      )}

      <div className="karte">
        <MenschKarte mensch={foto.mensch} uid={foto.beitragender} />
      </div>
    </div>
  )
}
